import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  FlexControlledForm,
  Preloader,
  Text,
  useDebounceState,
  WarningCard,
  WarningIcon,
} from '@common-fe/common-fe';
import _ from 'lodash';

import { ModalWrapper } from '@/components/wrappers';
import { Steps } from '@/modules/transaction/components/Statements/components/ChangeEmailModal/ChangeEmailModal.constants';
import useEditEmailFields, { EmailConfig } from '@/modules/transaction/components/Statements/components/ChangeEmailModal/hooks/useEditEmailFields';
import useEditEmailStore from '@/modules/transaction/components/Statements/components/ChangeEmailModal/stores/useEditEmail.store';
import useSendVerificationCodeQuery from '@/modules/user/components/Verification/queries/useSendVerificationCode.query';
import { VerificationErrorText, VerificationServerError, VerificationType } from '@/modules/user/components/Verification/verification.types';
import useUpdateEmployeeQuery from '@/modules/user/queries/useUpdateEmployee.query';
import useEmployeeStore from '@/modules/user/stores/useEmployee.store';
import { useValidationUtils } from '@/utils/hooks/useValidationUtils';

interface Props {
  onClose: () => void;
  setStep: (value: Steps) => void;
}

const ChangeEmailModal: React.FC<Props> = ({ onClose, setStep }) => {
  const { t } = useTranslation();
  const [isSubmit, setIsSubmit] = useState(false);
  const [errorText, setErrorText] = useState<string | undefined>(undefined);
  const fields = useEditEmailFields({ errorText });
  const { values, setValues } = useDebounceState<Partial<EmailConfig>>();
  const { employee, setEmployee } = useEmployeeStore();
  const { state, setState } = useEditEmailStore();
  const { getErrors, getValidateFields } = useValidationUtils();
  const { sendVerificationCode, isLoading: isCodeSending } = useSendVerificationCodeQuery();
  const { updateEmployee, isLoading } = useUpdateEmployeeQuery();

  const handleChangePreferredEmail = useCallback(async () => {
    await updateEmployee({ is_employee_email_preferred: state.isEmployeeEmailPreferred });
    setEmployee({ isEmployeeEmailPreferred: state.isEmployeeEmailPreferred });
  }, [updateEmployee, setEmployee, state.isEmployeeEmailPreferred ]);
  

  const handleSendVerificationCode = useCallback(async () => {
    if ((employee?.employeeProvidedEmail === values?.employeeProvidedEmail && employee?.isVerifiedEmployeeEmail)
      || !state?.isEmployeeEmailPreferred) {
      await handleChangePreferredEmail();
      setStep(Steps.SuccessfullyChanged);
      return;
    }
    await sendVerificationCode({
      type: VerificationType.EMAIL,
      value: values?.employeeProvidedEmail || '',
    });
    setState({ employeeProvidedEmail: values?.employeeProvidedEmail });
    setStep(Steps.VerifyEmail);
  }, [sendVerificationCode, setState, setStep, values, employee, state, handleChangePreferredEmail]);

  const handleSubmit = useCallback(async () => {
    setIsSubmit(true);
    setErrorText(undefined);
    const validateFields = getValidateFields(fields);
    const errors = getErrors(values, validateFields);

    if (!_.isEmpty(errors)) {
      return;
    }

    try {
      await handleSendVerificationCode();
    } catch (error) {
      const errorMessage = _.get(error, 'response.data.elevate_error_message', '') as string;

      if (errorMessage.includes(VerificationServerError.CODE_RESEND_TIMEOUT_ERROR)) {
        setErrorText(VerificationErrorText.CODE_RESEND_TIMEOUT_ERROR);
        return;
      }  

      setErrorText(VerificationErrorText.OTHER_ERROR);
    }
  }, [values, getErrors, getValidateFields, fields, handleSendVerificationCode]);

  useEffect(() => {
    if (errorText) {
      setErrorText(undefined);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.employeeProvidedEmail]);

  return (
    <ModalWrapper
      visible
      onSetVisible={onClose}
      testId="edit-email"
    >
      <Box align="center">
        <Text size="2xl" weight={700}>
          {t('Would like to change your Email?')}
        </Text>
      </Box>

      <Box
        margin={{ top: 'spacing24' }}
        round="moduleRound"
        background="module"
        pad="spacing24"
      >
        <Box
          background="canvas"
          round="container1Round"
        >
          <FlexControlledForm
            key={`${state?.isEmployeeEmailPreferred}`}
            fields={fields}
            showError={isSubmit}
            onChangeValues={setValues}
            isModalType
            editMode
          />
        </Box>

        <Box margin={{ top: 'spacing24' }}>
          <WarningCard
            icon={<WarningIcon />}
            title={(
              <Text>
                {t('Updating your email address here will also impact all account-related communications and services.')}
              </Text>
            )}
            topBorderMargin="spacing12"
            pad="spacing12"
            background="canvas"
            containerProps={{ pad: { horizontal: 'spacing24', vertical: 'spacing16' } }}
          />
        </Box>
      </Box>

      <Box margin={{ top: 'spacing24' }} direction="row" justify="end">
        <Box>
          <AppButton
            width="160px"
            buttonType="secondary"
            testId="on-close"
            onClick={onClose}
          >
            {t('Cancel')}
          </AppButton>
        </Box>
        <Box margin={{ left: 'spacing12' }}>
          <AppButton
            width="160px"
            testId="submit"
            onClick={handleSubmit}
          >
            {(isCodeSending || isLoading) ? <Preloader color="white" /> : t('Save Changes')}
          </AppButton>
        </Box>
      </Box>
    </ModalWrapper>
  );
};

export default ChangeEmailModal;
